//TODO: https://uberall.atlassian.net/browse/REP-5098
/* istanbul ignore file -- @preserve */
import React, { useEffect, useMemo } from 'react';
import { Model } from 'survey-core';
import { Survey } from 'survey-react-ui';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import 'survey-core/survey-core.min.css';
import styles from './survey.module.css';
import sharedStyles from '../../sharedStyles.module.css';
import useRequestSurveyForm from '../../api/hooks/useFetchSurveyForm';
import Loader from '../loader';
import { SubmitSurveyFormResponsePayload } from '../../types/survey';
import { useSubmitSurveyResponse } from '../../api/hooks/useSubmitSurveyForm';
import { SURVEY_FORM_NAMESPACE } from '../../constants';
import UnpublishedSurvey from '../unpublished-survey';

const SurveyFormComponent: React.FC = () => {
    const [searchParams] = useSearchParams();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const uniqueId = searchParams.get('id');
    const { mutate: submitSurveyResponse } = useSubmitSurveyResponse();
    const { data, loading, error } = useRequestSurveyForm(uniqueId);

    const surveyModel = useMemo(() => {
        if (!data) {
            return null;
        }
        const model = new Model(data.surveyData);
        return model;
    }, [data]);

    useEffect(() => {
        if (!surveyModel || !uniqueId) {
            return;
        }

        //TODO: https://uberall.atlassian.net/browse/REP-5098
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const handleSurveyComplete = (sender: any, options: any) => {
            //TODO: value not used for V1 MVP, but will be used in event of editing response or preventing user from spamming responses
            const clientId = Date.now().toString(36) + Math.random().toString(36).substring(2, 15);

            const payload: SubmitSurveyFormResponsePayload = {
                clientId: clientId,
                uniqueId: uniqueId,
                surveyBody: JSON.stringify(sender.data, null, 3),
            };

            if (window.USER_SET_API_ENV === 'dev') {
                // eslint-disable-next-line no-console
                console.log('🚀 ~ handleSurveyComplete:', payload, options);
            }

            //INFO: Notify SurveyJS that data saving is in progress. It will show a "Saving..." message.
            options.showDataSaving();

            submitSurveyResponse(payload, {
                onSuccess: () => {
                    //INFO: Notify SurveyJS that data saving was successful.
                    options.showDataSavingSuccess();
                },
                onError: (err) => {
                    if (window.USER_SET_API_ENV === 'dev') {
                        // eslint-disable-next-line no-console
                        console.error('🚀 ~ Error:', err);
                    }
                    queryClient.invalidateQueries({ queryKey: [SURVEY_FORM_NAMESPACE, uniqueId] });
                    navigate('/survey/error', { replace: true });
                },
            });
        };

        surveyModel.onComplete.add(handleSurveyComplete);

        return () => {
            surveyModel.onComplete.remove(handleSurveyComplete);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [surveyModel, uniqueId, submitSurveyResponse]);

    if (!uniqueId) {
        return <Navigate to="/survey/error" replace={true} />;
    }

    if (loading) {
        return <Loader />;
    }

    if (data?.unpublishedMessage && !data.surveyData) {
        return <UnpublishedSurvey title={data.title ?? ''} message={data.unpublishedMessage} />;
    }

    if (error || !data || !surveyModel) {
        return <Navigate to="/survey/error" replace={true} />;
    }

    return (
        <>
            <div
                className={`${sharedStyles.boxContainer} ${styles.container}`}
                data-testid="survey-container"
            >
                <Survey model={surveyModel} />
            </div>
        </>
    );
};

export default SurveyFormComponent;
